// Draft ─ Reserved ─ Out ─ Returned. Tap a done stage → when (location time
// zone) + who + how, from order.events (rental_order_events). Orders older
// than event tracking show "before tracking" except Draft (created_at).
import React, { useEffect, useRef, useState } from 'react';
import { STAGES, STATUS_LABEL, ACTION_LABEL, fmtWhen, eventForStage } from './pipeline';

export default function StageStrip({ order, compact = false }) {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (open === null) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  if (!order) return null;

  const tz = order.rental_locations?.timezone;
  const cancelled = order.status === 'cancelled';
  const currentIdx = STAGES.indexOf(order.status);
  const cancelEvent = cancelled ? eventForStage(order, 'cancelled') : null;
  // Where the cancel happened: after the last stage that has an event or, failing that, after Draft.
  const reachedIdx = cancelled
    ? Math.max(0, ...STAGES.map((s, i) => (i > 0 && eventForStage(order, s)) ? i : 0))
    : currentIdx;

  const stages = STAGES.map((s, i) => {
    const done = cancelled ? i <= reachedIdx : i <= currentIdx;
    const current = !cancelled && i === currentIdx;
    const ev = i === 0 ? null : eventForStage(order, s);
    return { key: s, label: STATUS_LABEL[s], done, current, ev, hidden: cancelled && i > reachedIdx };
  });
  if (cancelled) stages.splice(reachedIdx + 1, stages.length, { key: 'cancelled', label: 'Cancelled', done: true, current: true, ev: cancelEvent, danger: true });

  const dot = (st) => ({
    width: compact ? 10 : 14, height: compact ? 10 : 14, borderRadius: '50%', flexShrink: 0,
    background: st.done ? (st.danger ? '#f87171' : '#f97316') : '#1e2235',
    border: `2px solid ${st.done ? (st.danger ? '#f87171' : '#f97316') : '#3a3f60'}`,
    boxShadow: st.current ? `0 0 0 4px ${st.danger ? 'rgba(248,113,113,0.25)' : 'rgba(249,115,22,0.25)'}` : 'none',
  });

  const popover = (st) => {
    const when = st.key === 'draft' ? order.created_at : st.ev?.created_at;
    const who  = st.key === 'draft' ? (order.events?.find(e => e.action === 'create')?.by_email || null) : st.ev?.by_email;
    const how  = st.key === 'draft' ? 'created' : (st.ev ? ACTION_LABEL[st.ev.action] || st.ev.action : null);
    return (
      <div role="dialog" data-testid="stage-popover" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 8, background: '#1e2235', border: '1px solid #333', borderRadius: 8, padding: '10px 12px', zIndex: 3000, minWidth: 220, boxShadow: '0 6px 18px rgba(0,0,0,0.45)', fontSize: 12, color: '#e2e8f0' }}>
        <div style={{ fontWeight: 800, marginBottom: 4 }}>{st.label}</div>
        {when
          ? <>
              <div>{fmtWhen(when, tz)}{tz ? <span style={{ color: '#64748b' }}> · {tz}</span> : null}</div>
              <div style={{ color: '#94a3b8', marginTop: 2 }}>{who ? `by ${who}` : 'by system'}{how ? ` · ${how}` : ''}</div>
              {st.ev?.reason && <div style={{ color: '#fbbf24', marginTop: 4 }}>“{st.ev.reason}”</div>}
            </>
          : <div style={{ color: '#94a3b8' }}>before tracking</div>}
      </div>
    );
  };

  return (
    <div ref={ref} data-testid="stage-strip" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: compact ? 4 : 0, position: 'relative', padding: compact ? '4px 0' : '10px 0' }}>
      {stages.map((st, i) => (
        <React.Fragment key={st.key}>
          {i > 0 && <div style={{ flex: '1 1 18px', minWidth: 12, height: 2, background: st.done ? (st.danger ? '#f87171' : '#f97316') : '#2a2d3e', margin: '0 4px' }} />}
          <div style={{ position: 'relative' }}>
            <button type="button" data-testid={`stage-${st.key}`} data-done={st.done ? '1' : '0'}
              disabled={!st.done}
              onClick={() => setOpen(open === st.key ? null : st.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: '4px 2px', cursor: st.done ? 'pointer' : 'default', color: st.done ? (st.danger ? '#f87171' : '#f1f5f9') : '#64748b', fontSize: compact ? 11 : 12, fontWeight: st.current ? 800 : 600 }}>
              <span style={dot(st)} />
              <span>{st.label}</span>
            </button>
            {open === st.key && popover(st)}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
